/**
 * Figures on this side (SR-222 block F5): the words the table and a cell's history draw
 * survey-api's vocabularies in, and the name of the survey a part's figures and rankings are kept
 * under.
 *
 * **Which survey a part's figures are kept under is the wire's** (served by survey-api `30d88f37`,
 * adopted by block F12): the table and the ranking catalogue carry `keptUnder` — `CANADA` for
 * `CANADA_FR`, every other survey itself. The mirror that stood here, `FIGURE_SURVEY_OF`, and its
 * twin `RANKING_SURVEY_OF` are gone with the gap. Rejected: keeping a presence list of the
 * catalogue's surveys to decide which parts draw figures — the mirror back under another name
 * (F12's Q1): every part draws the fold, and a survey with no catalogue says so inside it.
 *
 * **These are words, not mirrors.** A value they lack is drawn as spelled (`wordFor`), never
 * dropped — survey-api's scopes, origins, event kinds and surveys are open sets on this side.
 *
 * **`changeText` is display, not a rule.** survey-api computes `change` (the fraction against the
 * same cell a year earlier, rounded to four places); this side only writes it as a signed percent.
 * Rejected: recomputing it from the rows drawn — a second computation that would disagree with
 * the server's the first time a row is missing from the page.
 */

/** The name of a survey another's figures or placements are kept under, where a part says so
 *  ("Kept under the Canada survey") — one table for Asset & production and Rankings. `CANADA`
 *  alone today, the one survey survey-api keeps another's under; a code it lacks is drawn as
 *  spelled. */
export const KEPT_UNDER_WORDS: ReadonlyMap<string, string> = new Map([['CANADA', 'Canada']]);

/** What a derived field's "Edit in …" says where its source names a survey other than the one the
 *  part's block keeps its data under (F12's R2: the field's `source.surveyCode` against the read's
 *  `keptUnder`, never a table) — nothing is focused, since no cell here is the field's. */
export function notKeptHere(surveyCode: string, what: 'figures' | 'rankings'): string {
  return `Derived from ${wordFor(KEPT_UNDER_WORDS, surveyCode)}’s ${what}, not kept under this part`;
}

/** A column's scope, beneath its label. */
export const SCOPE_WORDS: ReadonlyMap<string, string> = new Map([
  ['individual', 'Individual'],
  ['team', 'Team'],
  ['firm', 'Firm']
]);

/** Where a cell's value came from, as its history says above the rows: the source of the
 *  journal row that is the value (SR-451) — `answer_prior` a year before the cycle's in a later
 *  response's multi-year table, the weakest word on that year. `freeze` is an older API's. */
export const ORIGIN_WORDS: ReadonlyMap<string, string> = new Map([
  ['answer', 'From the advisor’s answer'],
  ['correction', 'From a corrected answer'],
  ['answer_prior', 'From an earlier year of a later answer'],
  ['staff', 'Entered by staff'],
  ['firm_import', 'From a firm’s file'],
  ['import_reversal', 'Restored by an import’s reversal'],
  ['migration', 'Imported from Salesforce'],
  ['freeze', 'Frozen from a response']
]);

/** What one row of a cell's history did — its source (SR-451); a Veritant row with no value is a
 *  clear, said by `figureEventWords`. The last three are an older API's event kinds. */
const FIGURE_EVENT_WORDS: ReadonlyMap<string, string> = new Map([
  ['answer', 'Answered'],
  ['correction', 'Answer corrected'],
  ['answer_prior', 'Answered as an earlier year'],
  ['staff', 'Set'],
  ['firm_import', 'Firm’s file'],
  ['import_reversal', 'Import reversed'],
  ['migration', 'Imported from Salesforce'],
  ['confirmed', 'Confirmed unchanged'],
  ['frozen', 'Frozen'],
  ['value_set', 'Set'],
  ['value_cleared', 'Cleared']
]);

/** A history row's words: "Cleared" for a row that holds no value, else its source's. Only
 *  Veritant's rows may hold none (survey-api's `ck_metric_event_refs`), and a confirmation, which
 *  is no clear. */
export function figureEventWords(kind: string, value: Nullable<number>): string {
  return value === null && kind !== 'confirmed' ? 'Cleared' : wordFor(FIGURE_EVENT_WORDS, kind);
}

/** `table`'s words for `code`, or the code as spelled where it has none. */
export function wordFor(table: ReadonlyMap<string, string>, code: string): string {
  return table.get(code) ?? code;
}

/** survey-api's `change` fraction as a signed percent, two places: `0.0831` → "+8.31%",
 *  `-0.032` → "−3.20%" (a minus sign, not a hyphen), `0` → "0.00%". */
export function changeText(fraction: number): string {
  const percent = (Math.abs(fraction) * 100).toFixed(2);
  if (Number(percent) === 0) return '0.00%';
  return `${fraction < 0 ? '−' : '+'}${percent}%`;
}
